import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Dialog from 'components/Dialog';
import { useForm, useTranslation } from 'hooks';
import fileClient from 'communication/fileClient';
import DeleteDialogFooter from '../../../DeleteDialogFooter';
import Form from './Form';
import { styled } from '@mui/material/styles';

const colors = ['#c0cbd7', '#dddddd', '#ffffff', '#9da7a2', '#e6e9e6', '#c31924', '#237ec5'];

const PaperWrapper = styled('div')(() => ({
  maxWidth: '665px'
}));

const components = {
  Footer: DeleteDialogFooter
};

function Editor({ onClose, manager_id, templates }) {
  const { t } = useTranslation();
  const location = useLocation();

  const [selectedDesign, setSelectedDesign] = useState(1);
  const [selectedColor, setSelectedColor] = useState('#c0cbd7');

  const { values, useInput, handleReset } = useForm({
    initialValues: useMemo(() => ({ cvType: 'short', language: '100' }), [])
  });

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const { value: cvType } = useInput('cvType');
  const { value: language } = useInput('language');

  const handleSubmit = () => {
    fileClient.download(
      `/api/v1/manager/pdf/file/${language}/${cvType}/${selectedDesign}/${selectedColor.substring(
        1
      )}/${manager_id}`
    );
    handleClose();
  };

  const disableCreateCVButton = useMemo(() => !(Object.keys(values).length === 2), [values]);

  // ✅ Only open modal if the current URL matches /resume/pdfcreation
  const isOpen = location.pathname.includes('/resume/pdfcreation');

  return (
    <Dialog
      actionLabel={t('button.createCV')}
      component="a"
      disabled={disableCreateCVButton}
      open={isOpen}
      title={t('title.pdfConfigTitle')}
      onAction={handleSubmit}
      onClose={handleClose}
      maxWidth="sm"
      PaperProps={{
        style: { maxWidth: 680, width: '100%' }
      }}
      footerProps={{
        disableBorder: 'none'
      }}
      classes={{ paper: PaperWrapper }}
      overflowX="hidden"
      components={components}
    >
      <Form
        data-test-id="create-cv-form"
        useInput={useInput}
        selectedDesign={selectedDesign}
        selectedColor={selectedColor}
        setSelectedDesign={setSelectedDesign}
        setSelectedColor={setSelectedColor}
        colors={colors}
        templates={Array.isArray(templates) ? templates : Object.values(templates || {})}
      />
    </Dialog>
  );
}

Editor.propTypes = {
  onClose: PropTypes.func.isRequired,
  manager_id: PropTypes.string,
  templates: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
};

export default Editor;
